import { FunctionComponent, PropsWithChildren } from 'react';
import { v4 as uuid } from 'uuid';
import { useAuthentication } from '@/hooks/useAuthentication';
import { useNotification } from '@starjack/features/notifications';
import { ConditionLink, ContingentCondition } from '@/models/conditionLink';
import { usePostOrPutConditionLinkMutation } from '@/api/v2/conditionLinks';
import { allEnvironments } from '@/models/product';
import { ContingentConditionForm } from '@/pages/rulesets/components/conditions/ContingentConditionForm';

const buildInitialContingentCondition = (): ContingentCondition => ({
    id: 'new-' + uuid(),
    name: '',
    externalName: {
        de: '',
        en: '',
    },

    sort: undefined,
    minDaysBeforeTicketStart: null,

    validFrom: null,
    validTo: null,
    startDateValidFrom: null,
    startDateValidTo: null,
    validWeekdays: new Set<string>(),
    startDateValidWeekdays: new Set<string>(),
    personTypeIds: [],
    sellingGroup: null,
    notSellingGroup: null,
    maxTicketsPerStartDay: 0,
    maxTicketsForDateRange: null,
    contingentRangeFrom: null,
    contingentRangeTo: null,
    environments: allEnvironments,
    contingentsProductIdSpecific: true,
    userIds: [],
});

type ContingentConditionDialogContainerProps = {
    onClose: () => void;
    conditionLink: ConditionLink;
    condition?: ContingentCondition;
    createNewConditionOptions?: {
        insertAtIndex?: number;
    };
    onUpdateConditionLink: (conditionLink: ConditionLink) => void;
};

export const ContingentConditionFormContainer: FunctionComponent<
    PropsWithChildren<ContingentConditionDialogContainerProps>
> = ({
    condition,
    conditionLink,
    onClose,
    onUpdateConditionLink,
    createNewConditionOptions = {},
}) => {
    const {
        liftOperatorData: { liftOperatorId },
    } = useAuthentication();
    const { addNotification } = useNotification();
    const [apiAction, { isLoading }] = usePostOrPutConditionLinkMutation({
        liftOperatorId,
        conditionLink,
    });

    const isNewCondition = !condition?.id;

    // save or update logic
    const saveOrUpdate = (nextCondition: ContingentCondition) => {
        const nextConditionLink = {
            ...conditionLink,
        };
        if (!isNewCondition) {
            const conditionIndex = conditionLink.contingentConditions.findIndex(
                (c) => condition.id === c.id
            );

            if (conditionIndex > -1) {
                const contingentConditions = [
                    ...conditionLink.contingentConditions,
                ];

                contingentConditions.splice(conditionIndex, 1, nextCondition);

                nextConditionLink.contingentConditions = contingentConditions;
            }
        } else {
            const positionToInsert =
                createNewConditionOptions.insertAtIndex > -1
                    ? createNewConditionOptions.insertAtIndex
                    : nextConditionLink.contingentConditions.length;

            const nextConditions = [...nextConditionLink.contingentConditions];
            nextConditions.splice(positionToInsert, 0, nextCondition);

            nextConditionLink.contingentConditions = nextConditions.map(
                (nC, index) => ({
                    ...nC,
                    sort: index,
                })
            );
        }

        apiAction({ liftOperatorId, conditionLink: nextConditionLink })
            .unwrap()
            .then((nextConditionLink) => {
                addNotification({
                    message: 'Regel gespeichert',
                    type: 'success',
                });
                onUpdateConditionLink(nextConditionLink);
                onClose();
            })
            .catch(() => {
                addNotification({
                    message: 'Fehler beim Speichern',
                    type: 'error',
                });
            });
    };

    return (
        <ContingentConditionForm
            condition={condition || buildInitialContingentCondition()}
            isNewCondition={isNewCondition}
            onSave={saveOrUpdate}
            onClose={onClose}
            isLoading={isLoading}
        />
    );
};
